<template>
  <section class="deduction-panel" aria-label="차감 내역">
    <header class="panel-header">
      <span><i class="el-icon-tickets"></i>{{ $t('차감 내역') }}</span>
      <span class="deposit-context">{{ $t('정산 예상') }} {{ formatCurrency(expectedSettlement) }}</span>
      <el-radio-group v-model="viewMode" size="mini" aria-label="차감 내역 보기">
        <el-radio-button label="table">{{ $t('표') }}</el-radio-button>
        <el-radio-button label="waterfall" :disabled="compact">{{ $t('폭포형') }}</el-radio-button>
      </el-radio-group>
    </header>

    <div
      v-if="viewMode === 'waterfall' && !compact"
      ref="chart"
      class="waterfall"
      role="img"
      aria-label="차감 내역 폭포형 차트"></div>

    <template v-else>
      <!-- 가산(가게 수취) — 차감이 아니라 정산에 더해지는 항목을 물리적으로 분리 표기 -->
      <ul v-if="creditRows.length" class="credit-band" aria-label="가게 수취 항목">
        <li v-for="row in creditRows" :key="row.key" class="credit-item">
          <span class="credit-label"><i class="el-icon-top"></i>{{ row.label }}</span>
          <span class="credit-tag">{{ $t('가게 수취') }}</span>
          <span class="credit-amount">+{{ formatCurrency(row.magnitude) }}</span>
        </li>
      </ul>

      <el-table :data="debitRows" size="small" border :show-header="true" :empty-text="$t('데이타 없음')">
        <el-table-column :label="$t('차감 항목')" prop="label" min-width="140"></el-table-column>
        <el-table-column :label="$t('금액')" prop="magnitude" min-width="110" align="right">
          <template slot-scope="scope">-{{ formatCurrency(scope.row.magnitude) }}</template>
        </el-table-column>
        <el-table-column :label="$t('비중')" prop="share" width="90" align="right">
          <template slot-scope="scope">{{ formatShare(scope.row.magnitude) }}%</template>
        </el-table-column>
      </el-table>

      <!-- 매출 − 차감 + 수취 = 정산예상 정합을 한 줄로 확인 -->
      <p class="net-summary">
        <template v-if="Number(orderAmount) > 0">{{ $t('총매출') }} {{ formatCurrency(orderAmount) }}</template>
        <span v-if="creditSubtotal" class="net-credit"> + {{ $t('수취') }} {{ formatCurrency(creditSubtotal) }}</span>
        <span class="net-debit"> − {{ $t('차감') }} {{ formatCurrency(debitSubtotal) }}</span>
        <span class="net-result"> = {{ $t('정산 예상') }} {{ formatCurrency(expectedSettlement) }}</span>
      </p>
    </template>
  </section>
</template>

<script>
import {
  aggregateDeductionComponents,
  deductionComponentValue,
  deductionDirection,
  hasServerDeductionTotal,
  rowPlatform,
} from '@/utils/integratedSalesDeductions'

// 차감 표/폭포에 노출할 구성요소 정의(순서 = 폭포 진행 순서)
const COMPONENT_DEFS = [
  { key: 'delivery_fee', label: '배달요금' },
  { key: 'store_delivery_tip', label: '가게배달팁' },
  { key: 'service_commission', label: '중개수수료' },
  { key: 'card_commission', label: '결제대행' },
  { key: 'platform_discount', label: '플랫폼 할인' },
  { key: 'store_discount', label: '가게부담 할인' },
  { key: 'vat', label: '부가세' },
  { key: 'ad_cost', label: '광고비' },
  { key: 'instant_discount', label: '즉시할인' },
]

export default {
  props: {
    deductions: {
      type: Object,
      default: () => ({}),
    },
    expectedSettlement: {
      type: [Number, String],
      default: 0,
    },
    orderAmount: {
      type: [Number, String],
      default: 0,
    },
    estimatedFee: {
      type: [Number, String],
      default: 0,
    },
    platform: {
      type: String,
      default: '',
    },
    deductionSources: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      viewMode: 'table',
      chart: null,
      viewportWidth: window.innerWidth,
    }
  },
  computed: {
    compact() {
      return this.viewportWidth < 768 || !window.echarts
    },
    resolvedDeductions() {
      if (hasServerDeductionTotal(this.deductions) || this.platform) {
        return this.deductions
      }
      if (this.deductionSources.length) {
        // 플랫폼 미필터(전체) 시 소스 행들의 부호 있는 구성요소를 합산 → 순액(가산 상쇄 반영)
        return aggregateDeductionComponents(this.deductionSources)
      }
      return {}
    },
    serverSigned() {
      return hasServerDeductionTotal(this.deductions) || (!this.platform && this.deductionSources.length > 0)
    },
    // 모든 구성요소를 부호로 가산/차감 분류(0원 항목 제외)
    components() {
      const platform = rowPlatform(this.resolvedDeductions, this.platform)
      const list = COMPONENT_DEFS.map((def) => {
        const value = Number(
          deductionComponentValue(this.resolvedDeductions, def.key, platform, this.serverSigned) || 0
        )
        return {
          key: def.key,
          label: this.$t(def.label),
          value,
          magnitude: Math.abs(value),
          direction: deductionDirection(value),
        }
      }).filter((row) => row.value !== 0)

      const estimated = Number(this.estimatedFee) || 0
      if (estimated > 0) {
        list.push({
          key: 'estimated_fee',
          label: this.$t('미수집 38% 추정'),
          value: estimated,
          magnitude: estimated,
          direction: 'debit',
        })
      }
      return list
    },
    creditRows() {
      return this.components.filter((row) => row.direction === 'credit')
    },
    debitRows() {
      return this.components.filter((row) => row.direction === 'debit')
    },
    creditSubtotal() {
      return this.creditRows.reduce((sum, row) => sum + row.magnitude, 0)
    },
    debitSubtotal() {
      return this.debitRows.reduce((sum, row) => sum + row.magnitude, 0)
    },
  },
  watch: {
    deductions: {
      deep: true,
      handler() {
        this.$nextTick(this.renderChart)
      },
    },
    viewMode() {
      this.$nextTick(this.renderChart)
    },
    compact(val) {
      if (val) this.viewMode = 'table'
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
    this.$nextTick(this.renderChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.chart) this.chart.dispose()
  },
  methods: {
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString()}원`
    },
    formatShare(magnitude) {
      // 차감끼리 100% — 가산(가게 수취)은 분모에서 제외해 직관적 구성비를 보인다
      if (!this.debitSubtotal) return '0.0'
      return ((Number(magnitude || 0) / this.debitSubtotal) * 100).toFixed(1)
    },
    onResize() {
      this.viewportWidth = window.innerWidth
      if (this.chart) this.chart.resize()
    },
    renderChart() {
      if (this.viewMode !== 'waterfall' || this.compact || !this.$refs.chart || !window.echarts) return
      if (this.chart) this.chart.dispose()
      this.chart = window.echarts.init(this.$refs.chart)

      const anchorColor = '#8895A7'
      const creditColor = '#2E9E5B'
      const debitColor = '#E5603A'
      const start = Number(this.orderAmount) || 0

      const categories = []
      const base = [] // 투명 받침(막대 하단 위치)
      const bars = [] // 표시 막대 높이(절대값)
      const meta = [] // 색/부호 표시용

      let running = start
      if (start > 0) {
        categories.push(this.$t('총매출'))
        base.push(0)
        bars.push(start)
        meta.push({ color: anchorColor, signed: start })
      }

      // 가산 먼저(위로 쌓고) → 차감(아래로 내림) 순서로 매출→정산예상 흐름 표현
      this.creditRows.forEach((row) => {
        categories.push(row.label)
        base.push(running)
        bars.push(row.magnitude)
        meta.push({ color: creditColor, signed: row.magnitude })
        running += row.magnitude
      })
      this.debitRows.forEach((row) => {
        running -= row.magnitude
        categories.push(row.label)
        base.push(running)
        bars.push(row.magnitude)
        meta.push({ color: debitColor, signed: -row.magnitude })
      })

      categories.push(this.$t('정산 예상'))
      base.push(0)
      bars.push(Math.max(running, 0))
      meta.push({ color: anchorColor, signed: Math.max(running, 0) })

      this.chart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const bar = params.find((item) => item.seriesName === '흐름')
            const idx = bar ? bar.dataIndex : params[0].dataIndex
            const signed = meta[idx] ? meta[idx].signed : 0
            const prefix = signed > 0 && meta[idx].color === creditColor ? '+' : signed < 0 ? '-' : ''
            return `${params[0].axisValue}<br/>${prefix}${this.formatCurrency(Math.abs(signed))}`
          },
        },
        grid: { left: 10, right: 10, top: 16, bottom: 30, containLabel: true },
        xAxis: { type: 'category', data: categories, axisLabel: { interval: 0, rotate: 20 } },
        yAxis: { type: 'value', axisLabel: { formatter: (value) => `${Math.round(value / 1000)}K` } },
        series: [
          { name: 'base', type: 'bar', stack: 'total', itemStyle: { color: 'transparent' }, data: base },
          {
            name: '흐름',
            type: 'bar',
            stack: 'total',
            itemStyle: { borderRadius: [4, 4, 0, 0] },
            data: bars.map((value, i) => ({ value, itemStyle: { color: meta[i].color } })),
          },
        ],
      })
    },
  },
}
</script>

<style scoped>
.deduction-panel {
  min-width: 0;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 16px;
  overflow: hidden;
  box-shadow: var(--shadow-card);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: var(--color-text-primary);
  font-size: 15px;
  font-weight: 600;
}

.panel-header span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.panel-header i {
  color: var(--color-primary);
}

.deposit-context {
  margin-left: auto;
  color: var(--color-text-secondary);
  font-size: 12px;
  font-weight: 600;
}

.credit-band {
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
}

.credit-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 6px;
  border: 1px solid var(--color-success, #2e9e5b);
  border-radius: var(--radius-md);
  background: rgba(46, 158, 91, 0.08);
  font-size: 13px;
}

.credit-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.credit-label i {
  color: var(--color-success, #2e9e5b);
}

.credit-tag {
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--color-success, #2e9e5b);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.credit-amount {
  margin-left: auto;
  color: var(--color-success, #2e9e5b);
  font-weight: 700;
}

.net-summary {
  margin: 10px 0 0;
  color: var(--color-text-secondary);
  font-size: 12px;
  line-height: 1.6;
}

.net-summary .net-credit {
  color: var(--color-success, #2e9e5b);
  font-weight: 600;
}

.net-summary .net-debit {
  color: var(--color-danger, #e5603a);
  font-weight: 600;
}

.net-summary .net-result {
  color: var(--color-text-primary);
  font-weight: 700;
}

.waterfall {
  width: 100%;
  height: 300px;
}

@media (max-width: 768px) {
  .panel-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
